import {
  autoUpdate,
  type FloatingContext,
  FloatingFocusManager,
  FloatingNode,
  FloatingOverlay,
  FloatingPortal,
  FloatingTree,
  flip,
  type Middleware,
  offset,
  type Placement,
  type ReferenceType,
  safePolygon,
  shift,
  size,
  type UseFloatingOptions,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useFocus,
  useHover,
  useInteractions,
  useTransitionStyles,
} from '@floating-ui/react';
import { trackDismissableElement } from '@zag-js/dismissable';
import React from 'react';
import { ChakraV3 } from '..';

export const FloatingPortalContent = (props: { children: JSX.Element; isPortal: boolean }) => {
  return props.isPortal ? (
    <FloatingPortal root={document.getElementById('floating-ui-portal-root')}>
      {props.children}
    </FloatingPortal>
  ) : (
    props.children
  );
};

export const FloatingFocusManagerContent = (props: {
  children: JSX.Element;
  context: FloatingContext<ReferenceType>;
  shouldInitialFocus: boolean;
  modal: boolean;
}) => {
  return props.shouldInitialFocus ? (
    <FloatingFocusManager
      context={props.context}
      modal={props.modal}
      order={['content']}
      returnFocus={false}
    >
      {props.children}
    </FloatingFocusManager>
  ) : (
    props.children
  );
};

export const FloatingOverlayContent = (props: { children: JSX.Element; isRoot?: boolean }) => {
  return props.isRoot ? (
    <FloatingOverlay lockScroll={false} style={{ zIndex: 1500 }}>
      {props.children}
    </FloatingOverlay>
  ) : (
    props.children
  );
};

interface FloatingPopoverProps {
  /** Whether the popover is enabled. When false, renders children without popover behavior. */
  enabled?: boolean;
  /** Controlled open state. */
  open?: boolean;
  /** Callback to update controlled open state. */
  setOpen?: (open: boolean) => void;
  /** Virtual reference element position, used when there is no DOM trigger element. */
  clientRect?: () => DOMRect;
  /** Render function for the popover content. */
  render: (data: { context: FloatingContext<ReferenceType> }) => React.ReactNode;
  /** Custom floating-ui middleware. Overrides the default flip/offset/shift/size middleware. */
  middleware?: Middleware[];
  /** Interaction that opens the popover. */
  trigger?: 'hover' | 'focus' | 'click';
  /** Trigger element that the popover is anchored to. */
  children?: JSX.Element;
  /** Whether this is a root-level popover. Controls overlay rendering and zag-js dismissable registration. Auto-wraps in FloatingTree when true. */
  isRoot?: boolean;
  /** Whether to render inside a FloatingPortal. */
  isPortal?: boolean;
  /** Whether to auto-focus the first tabbable element inside the popover on open. */
  shouldInitialFocus?: boolean;
  /** Whether to trap focus within the popover, looping Tab from the last to the first element. */
  modal?: boolean;
  /** Whether to close the popover when clicking outside. */
  closeOnBlur?: boolean;
  /** Whether to close the popover when scrolling outside. */
  closeOnScroll?: boolean;
  /** Preferred placement of the popover relative to the trigger. */
  placement?: Placement;
}

const FloatingPopoverInternal = (props: FloatingPopoverProps) => {
  const {
    middleware,
    children,
    isRoot,
    isPortal = false,
    shouldInitialFocus = false,
    modal = false,
    closeOnBlur = true,
    closeOnScroll = true,
    placement = 'bottom-start',
    render,
  } = props;
  const tree = useFloatingTree();
  const nodeId = useFloatingNodeId();

  const [isOpen, setIsOpen] = React.useState<boolean>(props.open || false);
  const open = props.open ?? isOpen;
  const trigger = props.trigger ?? 'click';

  const onOpenChange = React.useCallback(
    (value: boolean) => {
      props.setOpen ? props.setOpen(value) : setIsOpen(value);
    },
    [props],
  );

  const options: Partial<UseFloatingOptions> = {
    open: open,
    onOpenChange,
    nodeId,
    whileElementsMounted: autoUpdate,
    placement,
    strategy: 'fixed',
    middleware: middleware ?? [
      flip({
        fallbackPlacements: ['bottom-end', 'top-start', 'top-end', 'right-start', 'left-start'],
        fallbackStrategy: 'bestFit',
      }),
      offset(10),
      shift(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          });
        },
        padding: 10,
      }),
    ],
  };

  const {
    x,
    y,
    refs: { setFloating, setReference },
    strategy,
    context,
  } = useFloating(options);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, {
      enabled: trigger === 'click',
    }),
    useHover(context, {
      enabled: trigger === 'hover',
      handleClose: safePolygon({
        blockPointerEvents: false,
        buffer: 8,
        requireIntent: false,
      }),
      delay: {
        close: 300,
        open: 200,
      },
    }),
    useFocus(context, {
      enabled: trigger === 'focus',
      keyboardOnly: false,
    }),
    useDismiss(context, {
      ancestorScroll: closeOnScroll,
      outsidePress: closeOnBlur,
      bubbles: false,
    }),
  ]);

  // Register the floating element in the zag-js dismissable layer stack so that
  // Chakra Dialogs (which use zag-js) know this popover exists above them.
  // Without this, pressing Escape inside an open popover also closes the parent
  // Dialog because zag-js uses a capture-phase document listener that fires before
  // floating-ui's bubbling-phase listener, and the Dialog thinks it's the topmost layer.
  // Only register for root popovers to avoid interfering with nested Chakra components
  // (e.g. Menu) that render portal content outside the FloatingPopover's DOM.
  React.useEffect(() => {
    if (!open || !isRoot) {
      return;
    }

    const floatingEl = context.refs.floating.current;

    if (!floatingEl) {
      return;
    }

    return trackDismissableElement(floatingEl, {
      onDismiss: () => onOpenChange(false),
      type: 'popover',
      defer: true,
      onFocusOutside: (e) => {
        e.preventDefault();
        e.stopPropagation();
      },
    });
  }, [open, isRoot, context.refs.floating, onOpenChange]);

  React.useEffect(() => {
    if (props.clientRect) {
      setReference({
        getBoundingClientRect: props.clientRect,
      });
    }
  }, [props.clientRect, setReference]);

  const ref = setReference;

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: { open: 200, close: 100 },
    initial: { opacity: 0 },
  });

  // When Shift+Tab is pressed from the first focusable element,
  // return focus to the trigger element instead of escaping the popover.
  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      const el = context.refs.domReference.current as HTMLElement;

      if (e.shiftKey && e.key === 'Tab') {
        const hasOpenDescendents = !tree?.nodesRef.current.some(
          (node) => node.parentId === nodeId && node.context?.open,
        );

        if (modal || !hasOpenDescendents) {
          return;
        }

        el.focus();
      }
    },
    [context.refs.domReference, tree, nodeId, modal],
  );

  return (
    <FloatingNode id={nodeId}>
      {children && React.cloneElement(children, getReferenceProps({ ref, ...children.props }))}
      {isMounted && (
        <FloatingPortalContent isPortal={isPortal}>
          <FloatingOverlayContent isRoot={isRoot}>
            <FloatingFocusManagerContent
              context={context}
              shouldInitialFocus={shouldInitialFocus}
              modal={modal}
            >
              <div
                {...getFloatingProps({
                  ref: setFloating,
                  style: {
                    position: strategy,
                    top: y ?? 0,
                    left: x ?? 0,
                    boxSizing: 'border-box',
                    zIndex: 1500,
                    ...transitionStyles,
                  },
                })}
                onKeyDown={onKeyDown}
                data-scope='popover'
              >
                {render({ context })}
              </div>
            </FloatingFocusManagerContent>
          </FloatingOverlayContent>
        </FloatingPortalContent>
      )}
    </FloatingNode>
  );
};

export const FloatingPopover = (props: FloatingPopoverProps) => {
  const isRoot = props.isRoot ?? true;
  const enabled = props.enabled ?? true;
  const parentId = useFloatingParentNodeId();

  if (!enabled) {
    return <>{props.children}</>;
  }

  if (parentId == null) {
    return (
      <FloatingTree>
        <FloatingPopoverInternal {...props} isRoot={isRoot} />
      </FloatingTree>
    );
  }

  return <FloatingPopoverInternal {...props} />;
};

interface FloatingPopoverBodyProps extends ChakraV3.StackProps {
  title?: string;
}

export const FloatingPopoverBody = (props: React.PropsWithChildren<FloatingPopoverBodyProps>) => {
  const { title, children, ...rest } = props;

  return (
    <ChakraV3.Stack bg='white' shadow='xl' rounded='xl' p={4} {...rest}>
      {title && (
        <>
          <ChakraV3.Text fontWeight='semibold' fontSize='md'>
            {title}
          </ChakraV3.Text>
          <ChakraV3.Separator />
        </>
      )}
      {children}
    </ChakraV3.Stack>
  );
};
